import { BinaryWriter } from '@neo-one/client-common';
import { MapContractParameter } from '@neo-one/node-core';
import { utils } from '@neo-one/utils';
import _ from 'lodash';
import { ArrayStackItem } from './ArrayStackItem';
import { CircularReferenceError, InvalidValueBufferError, MissingStackItemKeyError } from './errors';
import { getNextID } from './referenceCounter';
import { StackItemBase } from './StackItemBase';
import { StackItemType } from './StackItemType';
export class MapStackItem extends StackItemBase {
    constructor({ referenceKeys = new Map(), referenceValues = new Map(), } = {}) {
        super();
        this.referenceID = getNextID();
        this.referenceKeys = referenceKeys;
        this.referenceValues = referenceValues;
    }
    toStructuralKey() {
        return `${this.referenceID}`;
    }
    equals(other) {
        return this === other;
    }
    isMap() {
        return false;
    }
    asBoolean() {
        return true;
    }
    asBuffer() {
        throw new InvalidValueBufferError();
    }
    toContractParameter(seen = new Set()) {
        if (seen.has(this)) {
            throw new CircularReferenceError();
        }
        const newSeen = new Set([...seen]);
        newSeen.add(this);
        return new MapContractParameter(this.keysArray().map((key) => [key.toContractParameter(newSeen), this.get(key).toContractParameter(newSeen)]));
    }
    get size() {
        return this.referenceKeys.size;
    }
    has(item) {
        const referenceKey = item.toStructuralKey();
        return this.referenceKeys.get(referenceKey) !== undefined;
    }
    get(item) {
        const referenceKey = item.toStructuralKey();
        const value = this.referenceValues.get(referenceKey);
        if (value === undefined) {
            throw new MissingStackItemKeyError();
        }
        return value;
    }
    set(key, value) {
        const referenceKey = key.toStructuralKey();
        this.referenceKeys.set(referenceKey, key);
        this.referenceValues.set(referenceKey, value);
        return this;
    }
    delete(item) {
        const referenceKey = item.toStructuralKey();
        this.referenceKeys.delete(referenceKey);
        this.referenceValues.delete(referenceKey);
        return this;
    }
    keys() {
        return new ArrayStackItem(this.keysArray());
    }
    keysArray() {
        return [...this.referenceKeys.values()];
    }
    valuesArray() {
        return [...this.referenceValues.values()];
    }
    asMapStackItem() {
        return this;
    }
    incrementInternal(seen) {
        return this.incrementInternalArray(this.keysArray(), seen) + this.incrementInternalArray(this.valuesArray(), seen);
    }
    decrementInternal(seen) {
        return this.decrementInternalArray(this.keysArray(), seen) + this.decrementInternalArray(this.valuesArray(), seen);
    }
    convertJSONInternal(seen) {
        return _.fromPairs(utils
            .zip(this.keysArray(), this.valuesArray())
            .map(([key, value]) => [JSON.stringify(key.convertJSON(seen)), value.convertJSON(seen)]));
    }
    serializeInternal(seen) {
        const writer = new BinaryWriter();
        writer.writeUInt8(StackItemType.Map);
        const keys = this.keysArray();
        writer.writeVarUIntLE(keys.length);
        keys.forEach((key) => {
            writer.writeBytes(key.serialize(seen));
            writer.writeBytes(this.get(key).serialize(seen));
        });
        return writer.toBuffer();
    }
}

//# sourceMappingURL=MapStackItem.js.map
